import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Carousel, { ControllerMixin } from 'nuka-carousel';
import Link from 'components/Link';
import buildAffiliateLink from 'lib/buildAffiliateLink';
import ProductSlide from './productSlide';
import constants from './constants';
import { slideMouseDownHandler } from './slideMouseDownHandler';

import styles from './styles.module.scss';

const {
  cellSpacing,
  gutterDesktop,
  slidesToShow,
  gridWidths,
  imageHeights,
  slideHeights,
  currencies,
} = constants;

// gets a value from objects/maps that you pass in.
// maps must have keys whose names are numbers corresponding to breakpoints
// returns the value for the largest key that the current window.innerWidth is larger than
const getValueByBreakpoint = (map) => {
  if (typeof window !== 'undefined') {
    return Object.entries(map).reduce((height, breakpoint) => (
      (window.innerWidth >= parseInt(breakpoint[0], 10))
        ? breakpoint[1]
        : height
    ), map['0']);
  }
  return map['0'];
};

// mimics the slide width calculation from nuka carousel so that we can get the slide width
// without waiting to check for it in componentDidMount
const getSlideWidth = () => {
  if (typeof window !== 'undefined') {
    return (document.body.clientWidth / getValueByBreakpoint(slidesToShow))
      - ((cellSpacing * (100 - (100 / getValueByBreakpoint(slidesToShow)))) / 100);
  }
  return 0;
};

// calculates and returns the left margin for the container element
const getContainerLeftMargin = () => {
  if (typeof window !== 'undefined') {
    const { innerWidth } = window;
    const { clientWidth } = document.body;
    const gridWidth = getValueByBreakpoint(gridWidths);
    if (innerWidth >= 1240) {
      return (gridWidth * 0.16667) + ((clientWidth - gridWidth) / 2) + 33;
    } if (innerWidth >= 1000) {
      return ((clientWidth - gridWidth) / 2) + gutterDesktop;
    }
  }
  return 0;
};

const getImageWidth = (image, slideWidth, imageHeight) => {
  if ((image.width / slideWidth) > (image.height / imageHeight)) {
    return image.width * (slideWidth / image.width);
  }
  return image.width * (imageHeight / image.height);
};

const processContent = (content) => {
  const { productList } = content;
  return {
    list: {
      listId: productList.id,
      listType: productList.type,
      listHeadline: productList.headline,
      items: productList.products,
    },
  };
};

class ArticleContentSlider extends Component {
  static propTypes = {
    content: PropTypes.shape({
      productList: PropTypes.shape({
        id: PropTypes.string,
        type: PropTypes.string,
        headline: PropTypes.shape({
          primary: PropTypes.string,
          seo: PropTypes.string,
          social: PropTypes.string,
          tease: PropTypes.string,
        }),
        products: PropTypes.arrayOf(PropTypes.shape({
          id: PropTypes.string,
          type: PropTypes.string,
          sponsoredBy: PropTypes.string,
          teaseImage: PropTypes.shape({
            id: PropTypes.string,
            headline: PropTypes.shape({
              primary: PropTypes.string,
            }),
            caption: PropTypes.string,
            url: PropTypes.shape({
              primary: PropTypes.string,
            }),
            height: PropTypes.number,
            width: PropTypes.number,
            graphicContent: PropTypes.bool,
          }),
          offers: PropTypes.arrayOf(PropTypes.shape({
            linkUrl: PropTypes.string.isRequired,
            price: PropTypes.string.isRequired,
            seller: PropTypes.shape({
              name: PropTypes.string.isRequired,
            }),
          })),
        })),
      }),
    }).isRequired,
  };

  constructor(props) {
    super(props);

    this.focusSlide = this.focusSlide.bind(this);
    this.getInitialState = ControllerMixin.getInitialState.bind(this);
    this.setCarouselData = ControllerMixin.setCarouselData.bind(this);
    this.onScrubberMouseDown = this.onScrubberMouseDown.bind(this);
    this.onScrubberMouseMove = this.onScrubberMouseMove.bind(this);
    this.onScrubberMouseUp = this.onScrubberMouseUp.bind(this);
    this.scrubber = this.scrubber.bind(this);
    this.bottomDecorator = this.bottomDecorator.bind(this);
    this.afterSlide = this.afterSlide.bind(this);

    const content = processContent(props.content);
    const itemsWithImages = content.list.items.filter((item) => (
      item.teaseImage && item.teaseImage.url && item.teaseImage.url.primary
    ));

    this.state = {
      container: {
        useMargin: false,
        leftMargin: 0,
        width: '100%',
      },
      slider: {
        slides: itemsWithImages.map(() => ({ imageWidth: 0 })),
        itemsWithImages,
        dragging: false,
        slideWidth: getSlideWidth(),
        slideHeight: getValueByBreakpoint(slideHeights),
        imageHeight: getValueByBreakpoint(imageHeights),
        activeSlideIndex: 0,
      },
      scrubber: {
        dragging: false,
        transitionPosition: false,
        xOrigin: 0,
        xOffset: 0,
        xPos: 0,
        leftMargin: 0,
      },
    };
  }

  componentDidMount() {
    const {
      scrubber: scrubberState,
      container: containerState,
      slider: sliderState,
    } = this.state;
    let containerWidth;
    let useMargin;
    const slideWidth = getSlideWidth();

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.onResize.bind(this)());
      containerWidth = document.body.clientWidth;
      useMargin = window.innerWidth > 999;
    }

    const slides = sliderState.itemsWithImages.map((item) => ({
      imageWidth: getImageWidth(item.teaseImage, slideWidth, sliderState.imageHeight),
    }));
    // eslint-disable-next-line react/no-did-mount-set-state
    this.setState({
      scrubber: {
        ...scrubberState,
        xPos: this.scrubberHandle.offsetLeft,
        leftMargin: this.scrubberHandle.offsetLeft,
        handleWidth: this.scrubberHandle.offsetWidth,
        trackWidth: this.scrubberTrack.offsetWidth,
      },
      container: {
        ...containerState,
        width: containerWidth || containerState.width,
        leftMargin: getContainerLeftMargin(),
        useMargin,
      },
      slider: {
        ...sliderState,
        slides,
        slideWidth,
      },
    });
  }

  componentDidUpdate(lastProps, lastState) {
    if (lastState.scrubber.trackWidth !== this.scrubberTrack.offsetWidth) {
      const { scrubber } = this.state;

      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        scrubber: {
          ...scrubber,
          trackWidth: this.scrubberTrack.offsetWidth,
          handleWidth: this.scrubberHandle.offsetWidth,
        },
      });
    }
  }

  onResize() {
    let running = false;
    const resizeHandler = () => {
      const {
        slider: sliderState,
        container: containerState,
      } = this.state;
      const slides = sliderState.itemsWithImages.map((item) => ({
        imageWidth: getImageWidth(item.teaseImage,
          sliderState.slideWidth,
          sliderState.imageHeight),
      }));

      this.setState({
        slider: {
          ...sliderState,
          slides,
          width: document.body.clientWidth,
          slideWidth: getSlideWidth(),
          slideHeight: getValueByBreakpoint(slideHeights),
          imageHeight: getValueByBreakpoint(imageHeights),
        },
        container: {
          ...containerState,
          width: document.body.clientWidth,
          leftMargin: getContainerLeftMargin(),
          useMargin: window.innerWidth >= getValueByBreakpoint(gridWidths),
        },
      });
      running = false;
    };

    return () => {
      if (!running) {
        running = true;
        window.requestAnimationFrame(resizeHandler);
      }
    };
  }

  onScrubberMouseUp() {
    document.removeEventListener('mousemove', this.onScrubberMouseMove);
    document.removeEventListener('mouseup', this.onScrubberMouseUp);

    const {
      scrubber: scrubberState,
      scrubber: { handleWidth, leftMargin, trackWidth },
      slider: sliderState,
    } = this.state;
    const finalXPos = scrubberState.xPos + scrubberState.xOffset;
    const upperLimitedFinalXPos = finalXPos >= (trackWidth - (handleWidth / 2))
      ? trackWidth - handleWidth
      : finalXPos;
    const limitedFinalXPos = upperLimitedFinalXPos <= -1
      ? 0
      : upperLimitedFinalXPos;
    const scrubberMidpoint = limitedFinalXPos + (handleWidth / 2);
    const activeSlideIndex = Math.floor((scrubberMidpoint - leftMargin) / handleWidth);
    const quantizedXPos = (handleWidth * activeSlideIndex) + leftMargin;

    this.carousel.goToSlide(activeSlideIndex);
    this.setState({
      scrubber: {
        ...scrubberState,
        dragging: false,
        finalXPos: quantizedXPos,
        xPos: quantizedXPos,
      },
      slider: {
        ...sliderState,
        activeSlideIndex,
      },
    });
  }

  onScrubberMouseDown(e) {
    document.addEventListener('mousemove', this.onScrubberMouseMove);
    document.addEventListener('mouseup', this.onScrubberMouseUp);
    const { scrubber } = this.state;

    this.setState({
      scrubber: {
        ...scrubber,
        dragging: true,
        xOrigin: e.screenX,
        xOffset: 0,
        finalXPos: null,
        trackWidth: this.scrubberTrack.offsetWidth,
        handleWidth: this.scrubberHandle.offsetWidth,
      },
    });
  }

  onScrubberMouseMove(e) {
    const {
      scrubber: scrubberState,
      scrubber: {
        dragging,
        xOrigin,
      },
    } = this.state;
    if (dragging) {
      this.setState({
        scrubber: {
          ...scrubberState,
          xOffset: e.screenX - xOrigin,
        },
      });
    }
  }

  afterSlide(nextSlideIndex) {
    // Timeout is necessary because we're overriding nuka-carousel's default
    // swipe / drag / click handling. There's no built in way to override it,
    // so we're just waiting until it starts and then running focusSlide,
    // which focuses the correct slide and handles associated state changes
    setTimeout(() => {
      this.focusSlide(Math.ceil(nextSlideIndex));
    }, 1);
  }

  scrubber() {
    const {
      scrubber: scrubberState,
      container: containerState,
      slider: { itemsWithImages },
    } = this.state;
    const {
      finalXPos,
      xPos,
      xOffset,
      transitionPosition,
      dragging,
    } = scrubberState;
    const {
      useMargin,
    } = containerState;
    const nextXPos = (finalXPos !== null)
      ? finalXPos
      : xPos + xOffset;
    const scrubberWidth = '100%';
    const scrubberTrackStyle = {
      ...useMargin && {
        width: scrubberWidth,
      },
    };

    if (transitionPosition) {
      setTimeout(() => {
        this.setState({
          scrubber: {
            ...scrubberState,
            transitionPosition: false,
          },
        });
      }, 250);
    }

    /* eslint-disable jsx-a11y/no-static-element-interactions */
    return (
      <div
        className={styles.scrubberTrack}
        style={scrubberTrackStyle}
        ref={(scrubberTrack) => { this.scrubberTrack = scrubberTrack; }}
      >
        <div
          className={classnames(
            styles.scrubberHandle,
            { dragging },
          )}
          ref={(scrubberHandle) => { this.scrubberHandle = scrubberHandle; }}
          style={{
            position: 'absolute',
            width: `${100 / itemsWithImages.length}%`,
            left: `${nextXPos}px`,
          }}
          onMouseDown={this.onScrubberMouseDown}
        />
      </div>
    );
    /* eslint-enable jsx-a11y/no-static-element-interactions */
  }

  detailContainer(activeSlideIndex) {
    const {
      slider: sliderState,
      slider: { itemsWithImages },
    } = this.state;
    const [firstOffer] = itemsWithImages[activeSlideIndex].offers;
    const seller = firstOffer.seller.name;

    return (
      <div
        className={styles.detailContainer}
        style={{
          width: `${sliderState.slides[activeSlideIndex].imageWidth}px`,
        }}
      >
        <Link
          href={buildAffiliateLink(firstOffer.externalUrl)}
          target="_blank"
          className={styles.productDetails}
        >
          <span className={styles.productTitle}>{itemsWithImages[activeSlideIndex].name}</span>
          <span className={styles.productPrice}>
            {currencies[firstOffer.priceCurrency] || '$'}
            {firstOffer.price}
&nbsp;
          </span>
          <span className={styles.productSeller}>
            &mdash;
            {seller}
          </span>
        </Link>
        <Link
          href={buildAffiliateLink(firstOffer.externalUrl)}
          target="_blank"
          className={styles.productCTALink}
        >
          <div className={styles.productCTA}>
            <span className={`${styles.iconShop} icon icon-shop`} />
            shop
          </div>
        </Link>
      </div>
    );
  }

  bottomDecorator() {
    const { slider } = this.state;
    return (
      <div className={styles.decoratorContainer}>
        {this.detailContainer(slider.activeSlideIndex)}
        {this.scrubber()}
      </div>
    );
  }

  focusSlide(nextSlideIndex) {
    const {
      scrubber: scrubberState,
      scrubber: { handleWidth },
      slider: sliderState,
    } = this.state;
    const quantizedXPos = nextSlideIndex * handleWidth;

    this.carousel.goToSlide(nextSlideIndex);
    this.setState({
      scrubber: {
        ...scrubberState,
        transitionPosition: true,
        finalXPos: quantizedXPos,
        xPos: quantizedXPos,
      },
      slider: {
        ...sliderState,
        activeSlideIndex: nextSlideIndex,
      },
    });
  }

  render() {
    const { slider, container } = this.state;
    const {
      itemsWithImages,
      activeSlideIndex,
      slideWidth,
      slideHeight,
      imageHeight,
    } = slider;
    const {
      width: containerWidth,
      leftMargin: containerLeftMargin,
      useMargin: useContainerMargin,
    } = container;
    const scrubberContainerStyle = {
      marginTop: '70px',
      marginBottom: '3px',
      position: 'relative',
    };
    const cellAlign = 'center';
    return (
      <div
        className={styles.container}
        style={{
          width: `${containerWidth}px`,
          ...useContainerMargin && { marginLeft: -containerLeftMargin },
        }}
      >
        <Carousel
          decorators={[
            {
              component: this.bottomDecorator,
              position: 'BottomCenter',
              style: scrubberContainerStyle,
            }]}
          ref={(carousel) => { this.carousel = carousel; }}
          slidesToShow={getValueByBreakpoint(slidesToShow)}
          cellAlign={cellAlign}
          cellSpacing={cellSpacing}
          afterSlide={this.afterSlide}
          dragging
          // eslint-disable-next-line react/jsx-no-bind
          data={this.setCarouselData.bind(this, this.carousel)}
        >
          {
            itemsWithImages.map((slide, index) => {
              if (slide.type === 'product') {
                return (
                  <ProductSlide
                    slide={slide}
                    index={index}
                    onSlideMouseDown={slideMouseDownHandler(this, this.focusSlide)}
                    slideWidth={slideWidth}
                    slideHeight={slideHeight}
                    imageHeight={imageHeight}
                    activeSlideIndex={activeSlideIndex}
                  />
                );
              }
              return <div />;
            })
          }
        </Carousel>
      </div>
    );
  }
}

export default ArticleContentSlider;
